import React, { useEffect, useState } from 'react'
import axios from 'axios'
import Layout from '../../../../components/Layout/Layout'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import BoxNoSelect from './BoxNoSelect'

const UserRackBoxSelect = () => {
  const navigate = useNavigate()

  const { id } = useParams()
  const [rackBoxData, setRackBoxData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [boxList, setBoxList] = useState([])
  const [selectedRack, setSelectedRack] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [loadingBoxList, setLoadingBoxList] = useState(false)
  const [openingRack, setOpeningRack] = useState(null)

  const fetchRackBoxData = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await axios.get(
        `/api/v1/user/stock-check/rack-box/${id}`,
        { timeout: 60000 }
      )
      setRackBoxData(Array.isArray(response.data) ? response.data : [])
    } catch (error) {
      setError('Failed to load Rackbox data')
      console.error('Error fetching rack box data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRackBoxData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const groupRackNumbersByAlphabet = (rackData) => {
    const groupedData = rackData.reduce((acc, rack) => {
      const firstLetter = (rack.rackNo || '?').charAt(0).toUpperCase()
      if (!acc[firstLetter]) {
        acc[firstLetter] = []
      }
      acc[firstLetter].push(rack)
      return acc
    }, {})
    return groupedData
  }

  const handleRackClick = async (rackNo) => {
    if (openingRack) return
    setOpeningRack(rackNo)
    setSelectedRack(rackNo)
    setBoxList([])
    setLoadingBoxList(true)
    // Open first - the spinner inside the modal is the loading feedback.
    setIsModalOpen(true)

    try {
      const response = await axios.get(
        `/api/v1/user/stock-check/rack-box/${id}/boxes/${rackNo}`,
        { timeout: 30000 }
      )
      const fetchedBoxList = Array.isArray(response.data) ? response.data : []

      // A rack with a single box goes straight in. Opening the modal first
      // made it flash on screen before navigating away.
      if (fetchedBoxList.length === 1) {
        const singleBoxNo = fetchedBoxList[0].boxNo || 'empty'
        setIsModalOpen(false)
        navigate(`/user/stock-check/store-room/${rackNo}/${singleBoxNo}/${id}`)
        return
      }

      setBoxList(fetchedBoxList)
    } catch (error) {
      // Previously only logged, so a failed fetch left an empty modal open and
      // looked like the rack genuinely had no boxes.
      console.error('Failed to fetch box list:', error)
      setIsModalOpen(false)
      setSelectedRack(null)
      toast.error(
        error.response?.data?.message || `Could not load boxes for ${rackNo}.`
      )
    } finally {
      setLoadingBoxList(false)
      setOpeningRack(null)
    }
  }

  // Close the modal
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setBoxList([])
    setSelectedRack(null)
  }

  return (
    <Layout title='Rack Box Data'>
      {/* Normal flow rather than position:fixed - the toolbar wraps on a
          handheld, and a fixed bar with a hard-coded paddingTop covered the
          first row of racks when it did. */}
      {/* pt / pb split so the bar can sit tighter at the bottom. */}
      <div className='shadow-sm px-3 px-md-4 pt-4 pb-3 bg-white d-flex align-items-center'>
        <div className='w-100 d-flex flex-wrap justify-content-between align-items-center gap-3'>
          {/* lh-1 so the unused descender space in the line box does not read
              as extra padding under the text. */}
          <div className='fw-bold fs-5 lh-1'>Select Rack No.</div>

          <button
            type='button'
            className='btn btn-secondary btn-sm'
            onClick={() => navigate('/user/stock-check')}
          >
            Back
          </button>
        </div>
      </div>

      <div className='container-fluid px-3 px-md-4 py-4'>
        {loading ? (
          <div className='text-center text-muted py-5'>
            <div className='spinner-border' role='status'>
              <span className='visually-hidden'>Loading…</span>
            </div>
          </div>
        ) : error ? (
          <div className='text-center py-5'>
            <div className='text-danger mb-3'>{error}</div>
            <button
              type='button'
              className='btn btn-secondary btn-sm'
              onClick={fetchRackBoxData}
            >
              Try again
            </button>
          </div>
        ) : rackBoxData && rackBoxData.length > 0 ? (
          Object.entries(groupRackNumbersByAlphabet(rackBoxData)).map(
            ([letter, racks]) => (
              <div className='mb-4' key={letter}>
                <div className='fw-bold text-muted mb-2'>{letter}</div>
                <div className='rack-grid'>
                  {racks.map((rack) => (
                    <button
                      key={rack.rackNo}
                      type='button'
                      className={`rack-tile${
                        rack.rackStatus === 1 ? ' is-checked' : ''
                      }`}
                      disabled={openingRack === rack.rackNo}
                      onClick={() => handleRackClick(rack.rackNo)}
                    >
                      {rack.rackNo}
                    </button>
                  ))}
                </div>
              </div>
            )
          )
        ) : (
          <div className='text-center text-muted border rounded py-5'>
            No records
          </div>
        )}

        <BoxNoSelect
          isOpen={isModalOpen}
          selectedRack={selectedRack}
          id={id}
          boxList={boxList}
          loading={loadingBoxList}
          onClose={handleCloseModal}
        />
      </div>

      <style>{`
        /* Fills the row on any width instead of fixed 150px buttons. */
        .rack-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
          gap: 0.85rem;
        }
        .rack-tile {
          padding: 0.6rem 0.25rem;
          font-weight: 600;
          border-radius: 0.375rem;
          border: 1px solid #198754;
          background-color: #fff;
          color: #198754;
          transition: background-color 0.15s ease, color 0.15s ease;
        }
        .rack-tile:hover:not(:disabled) {
          background-color: #198754;
          color: #fff;
        }
        /* Blue fill still means "already checked", same as before. */
        .rack-tile.is-checked {
          background-color: #0d6efd;
          border-color: #0d6efd;
          color: #fff;
        }
        .rack-tile.is-checked:hover:not(:disabled) {
          background-color: #0b5ed7;
        }
        .rack-tile:disabled {
          opacity: 0.6;
        }
      `}</style>
    </Layout>
  )
}

export default UserRackBoxSelect
